import { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate, NavLink } from 'react-router-dom';
import {
  ArrowLeft,
  MessageCircle,
  MessageSquare,
  Users,
  Calendar,
  Mail,
  BookOpen,
  Settings,
  BarChart3,
  PauseCircle,
  PlayCircle,
  Bot,
  Loader2
} from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/Badge';

export default function AgentDashboardLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  // Fetch agent from backend
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setIsLoading(true);
        const result = await api.getAgent(id);
        setAgent(result.agent);
      } catch (err) {
        console.error('Failed to fetch agent:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  const toggleAgentStatus = async () => {
    if (!agent || isTogglingStatus) return;

    setIsTogglingStatus(true);
    try {
      const isCurrentlyActive = agent.status === 'active' || agent.status === 'live';

      if (isCurrentlyActive) {
        await api.deactivateAgent(id);
        setAgent(prev => ({ ...prev, status: 'paused' }));
      } else {
        await api.launchAgent(id, {
          respond_to: agent.respond_to || 'all',
          whitelist: agent.whitelist || []
        });
        setAgent(prev => ({ ...prev, status: 'active' }));
      }
    } catch (err) {
      console.error('Failed to toggle agent status:', err);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-text-primary mb-2">Agent not found</h2>
          <p className="text-text-muted mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-primary hover:underline"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isLive = agent.status === 'active' || agent.status === 'live';

  const navItems = [
    { to: '', icon: MessageCircle, label: 'Local Simulator', end: true },
    { to: 'conversations', icon: MessageSquare, label: 'Conversations' },
    { to: 'leads', icon: Users, label: 'Leads' },
    { to: 'appointments', icon: Calendar, label: 'Appointments' },
    { to: 'emails', icon: Mail, label: 'Emails' },
    { to: 'knowledge', icon: BookOpen, label: 'Knowledge Base' },
    { to: 'settings', icon: Settings, label: 'Settings' },
    { to: 'analytics', icon: BarChart3, label: 'Analytics' },
  ];

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-border flex flex-col h-screen sticky top-0">
        {/* Back to Dashboard */}
        <div className="p-4 border-b border-border">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </button>
        </div>

        {/* Agent Info */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-text-primary truncate">{agent.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant={isLive ? 'success' : 'warning'}
                  size="small"
                  dot
                >
                  {isLive ? 'Live' : 'Paused'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={label}
              to={`/agent/${id}${to ? `/${to}` : ''}`}
              end={end}
              className={({ isActive }) => `
                sidebar-link
                ${isActive ? 'active' : ''}
              `}
            >
              <Icon className="w-5 h-5" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Pause/Resume */}
        <div className="p-4 border-t border-border">
          <button
            onClick={toggleAgentStatus}
            disabled={isTogglingStatus}
            className={`
              w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl
              font-medium transition-all duration-200 disabled:opacity-50
              ${isLive
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-success/10 text-success hover:bg-success/20'
              }
            `}
          >
            {isTogglingStatus ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{isLive ? 'Pausing...' : 'Resuming...'}</span>
              </>
            ) : isLive ? (
              <>
                <PauseCircle className="w-5 h-5" />
                <span>Pause Bot</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-5 h-5" />
                <span>Resume Bot</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet context={{ agent, setAgent }} />
      </main>
    </div>
  );
}
